import { useEffect } from 'react';
import useSWR from 'swr';
import type { FontOption } from '@/components/bundle/bundleTypes';
import { backendClient } from '@/lib/api-client';

/**
 * Fetches available font options from the API, injects @font-face rules
 * into the document head, and preloads all font files.
 */
export function useBundleFonts(): FontOption[] {
  const { data: fontOptions = [] } = useSWR<FontOption[]>(
    '/bundle/fonts',
    (url: string) => backendClient.get(url).then((r) => r.data),
    { revalidateOnFocus: false, dedupingInterval: 600000 },
  );

  useEffect(() => {
    if (fontOptions.length === 0 || !fontOptions[0].fontFamily) return;

    // Inject @font-face rules
    const styleId = 'bundle-dynamic-fonts';
    let style = document.getElementById(styleId) as HTMLStyleElement | null;
    if (!style) {
      style = document.createElement('style');
      style.id = styleId;
      document.head.appendChild(style);
    }
    const extractName = (fontFamily: string) => fontFamily.split(',')[0].trim().replace(/'/g, '');
    const rules = fontOptions.flatMap((f) => {
      const name = extractName(f.fontFamily);
      return [
        `@font-face { font-family: '${name}'; src: url('${f.files.regular}') format('truetype'); font-weight: 400; font-style: normal; font-display: swap; }`,
        `@font-face { font-family: '${name}'; src: url('${f.files.bold}') format('truetype'); font-weight: 700; font-style: normal; font-display: swap; }`,
        `@font-face { font-family: '${name}'; src: url('${f.files.italic}') format('truetype'); font-weight: 400; font-style: italic; font-display: swap; }`,
        `@font-face { font-family: '${name}'; src: url('${f.files.bold_italic}') format('truetype'); font-weight: 700; font-style: italic; font-display: swap; }`,
      ];
    });
    style.textContent = rules.join('\n');

    // Preload all font files so they're cached before the settings dialog opens
    if (!document.querySelector('link[data-bundle-fonts]')) {
      fontOptions.forEach((f) => {
        Object.values(f.files).forEach((url) => {
          const link = document.createElement('link');
          link.rel = 'preload';
          link.as = 'font';
          link.type = 'font/ttf';
          link.href = url;
          link.crossOrigin = 'anonymous';
          link.dataset.bundleFonts = '';
          document.head.appendChild(link);
        });
      });
    }
  }, [fontOptions]);

  return fontOptions;
}
